/**
 * Fictional interwar Central/Eastern-European and Baltic/Nordic names.
 *
 * Six regional *styles* (Germanic, Slavic, Baltic, Nordic, Danubian, Carpathian) each carry their
 * own roots, suffixes and joining habits. Every faction is assigned one style, and its country,
 * cities and regions are all built from it, so a nation's places sound related ("Dornheim",
 * "Kesslingen", "Brennau" …) while its neighbour sounds different ("Varnograd", "Zlatica" …).
 * Names are plain ASCII so every font and protocol path handles them. A blacklist keeps real
 * country names (and a few famous cities) out.
 */
import type { Rng } from '../core/rng';
import { stageRng } from './context';

export interface FactionIdentity {
  name: string;
  adjective: string;
  /** Faction colour (0xRRGGBB); real-world theatres set it, generated nations use FACTION_COLORS. */
  color?: number;
}

export interface NameStyle {
  readonly id: 'germanic' | 'slavic' | 'baltic' | 'nordic' | 'danubian' | 'carpathian';
  readonly roots: readonly string[];
  readonly suffixes: readonly string[];
  /** Suffixes favoured for ports. */
  readonly coastal: readonly string[];
  /** Suffixes favoured for river towns (fords, bridges). */
  readonly river: readonly string[];
  /** Prefixes fused onto a name ("Neu" + "dorf" → "Neudorf"). */
  readonly fused: readonly string[];
  /** Separate qualifier words ("Stara Zlatica", "Targu Lucesti"). */
  readonly qualifiers: readonly string[];
  /** Vowel inserted between two clashing consonant clusters. */
  readonly link: string;
  /** Consonants in a row at a root/suffix seam before `link` is inserted (compounding languages tolerate more). */
  readonly maxCluster: number;
  readonly landSuffixes: readonly string[];
  readonly regionSuffixes: readonly string[];
}

/** Space-separated word list → array (keeps the tables compact and formatter-proof). */
const words = (list: string): readonly string[] => list.split(' ');

export const STYLES: readonly NameStyle[] = [
  {
    id: 'germanic',
    roots: words(
      'Dorn Kess Brenn Ahl Alten Bern Eber Eisen Falken Grau Hagen Hart Hohen Kalt Kron Linden Marl Rab Rosen Salz Schon Stein Tann Wald Wart Weiss Wolfs Zell Ried Gries Hirsch Kirch Muhl Sonnen Treu Uhlen Vogel Werd Aschen Birken Buch Erl Fried Gold Hasel Lauen Oster Reichen Tiefen Wetter',
    ),
    suffixes: words(
      'heim ling au burg berg dorf feld hausen stadt stein wald hof tal rode bach brunn lingen ingen eck kirchen born weiler hain thal',
    ),
    coastal: words('hafen munde sund strand haven'),
    river: words('furt bruck au werder'),
    fused: words('Neu Alt Ober Nieder Klein Gross'),
    qualifiers: words('Bad Sankt'),
    link: 'en',
    maxCluster: 5,
    landSuffixes: words('mark land au ia'),
    regionSuffixes: words('mark gau land au wald'),
  },
  {
    id: 'slavic',
    roots: words(
      'Varn Zlat Bel Kras Tarn Vod Grod Mir Rad Stan Voln Dobr Lub Kam Bor Cher Drav Gor Jas Kol Lip Mal Nov Ostr Rov Sokol Svet Ugl Vel Vys Zagor Zel Zvon Lesn Brod Grab Hrad Klim Luk Pesk Prav Sel Strel Topol Vish Yar Dub Glin Kozl Mokr Pust Ryb Trav',
    ),
    suffixes: words(
      'grad ovo ov ica nik pol sk in ets ava ovec evo any ino ovka orod ice ovitsa ograd insk ovy ishte',
    ),
    coastal: words('morsk ograd ovets'),
    river: words('most brod ovo'),
    fused: [],
    qualifiers: words('Stara Nova Veliki Gorni Dolni Mala'),
    link: 'o',
    maxCluster: 3,
    landSuffixes: words('ia ovia ava ina ania'),
    regionSuffixes: words('ovina sko ina ia avia any'),
  },
  {
    id: 'baltic',
    roots: words(
      'Kald Vilk Rud Taur Sil Jur Aukst Dar Liep Mez Ragn Tals Valk Zarn Kurs Ras Saul Ger Pag Ank Birz Dub Ezer Gaig Jelg Kupr Laz Mar Plung Ramb Sven Alsk Brast Dziv Gaul Karv Lauk Medz Nerg Ped Rok Skaud Varl Zem',
    ),
    suffixes: words('as ai ava uva ene iai ups ciems pils iena uze ele ule ere ininkai kalns iski enai ava'),
    coastal: words('mala sala osta'),
    river: words('upe ava uva'),
    fused: words('Vec Jaun'),
    qualifiers: words('Naujoji Senoji'),
    link: 'i',
    maxCluster: 3,
    landSuffixes: words('ava uva ija ene'),
    regionSuffixes: words('ava ija ene uva ele aiciai'),
  },
  {
    id: 'nordic',
    roots: words(
      'Halv Skar Tor Ask Bjor Frey Grim Hald Kval Ring Sol Stav Vard Alv Bred Dal Eid Fjell Hav Hjal Karl Lang Mos Nor Rys Sand Sig Skag Stor Tjal Ulv Aur Brun Kol Ek Gran Jar Lek Nyk Ors Rod Sten Tved Vall',
    ),
    suffixes: words(
      'vik sund by holm stad berg dal havn rup strand heim vang lund nes fors borg hus sjo myr torp vall sater bo',
    ),
    coastal: words('vik havn sund nes strand holm'),
    river: words('fors elv bro'),
    fused: words('Norr Sor Vester Oster'),
    qualifiers: words('Nya Gamla Stora Lilla'),
    link: 'e',
    maxCluster: 5,
    landSuffixes: words('mark land heim ia'),
    regionSuffixes: words('land mark dal bygd heim lagen'),
  },
  {
    id: 'danubian',
    roots: words(
      'Szol Kesz Bala Hod Kalo Vesz Mezo Sarv Tata Zala Kor Heves Csong Szent Toke Fony Gyom Harka Jasz Kapos Kun Mako Nyir Rakos Siklo Soly Bogar Dersz Ercs Fejer Godo Ilon Karca Lenti Pomaz Szeker Taro Varsz Zsam',
    ),
    suffixes: words(
      'var falva hely egy eny haza szeg lak vasar ret tok keszi fa halom hida szallas telek dorog egyhaza sziget',
    ),
    coastal: words('part rev sziget'),
    river: words('rev hida vize'),
    fused: words('Nagy Kis Also Felso Uj'),
    qualifiers: [],
    link: 'a',
    maxCluster: 3,
    landSuffixes: words('ia any eny sag'),
    regionSuffixes: words('sag koz hat seg'),
  },
  {
    id: 'carpathian',
    roots: words(
      'Luc Cal Brasc Tur Sib Mor Fag Dev Arg Bist Cern Flor Gal Olt Prah Rad Sal Tar Vasl Zar Bac Cor Dob Ilv Lup Mag Pet Sev Tec Alb Buz Caras Dumb Foc Giur Hat Marg Neam Pesc Rasn Salc Tism Val Vrat',
    ),
    suffixes: words('esti eni ani ova iu ea ara ita easa ul ina oara anu ata uta'),
    coastal: words('mare ita'),
    river: words('eni esti'),
    fused: [],
    qualifiers: words('Targu Campu Valea Podu'),
    link: 'i',
    maxCluster: 2,
    landSuffixes: words('ia ara ina ovia'),
    regionSuffixes: words('ia ara ana ina eni'),
  },
];

/** Real countries (current and historic) and demonyms — never generated as faction names. */
const REAL_WORDS = new Set(
  (
    'albania andorra armenia austria azerbaijan belarus belgium bosnia bulgaria croatia cyprus czechia denmark ' +
    'estonia finland france georgia germany greece hungary iceland ireland italy kosovo latvia liechtenstein ' +
    'lithuania luxembourg malta moldova monaco montenegro macedonia norway poland portugal romania russia ' +
    'serbia slovakia slovenia spain sweden switzerland turkey ukraine prussia bohemia moravia silesia galicia ' +
    'livonia courland ruthenia bavaria saxony wallachia transylvania dalmatia pomerania lusatia bukovina ' +
    'bessarabia podolia volhynia ingria karelia lapland carinthia styria tyrol slavonia serbian latvian ' +
    'lithuanian estonian polish russian austrian german hungarian romanian croatian slovak czech danish ' +
    'swedish norwegian finnish prussian bohemian moravian bavarian saxon galician livonian karelian ' +
    'russia ruthenian armenian georgian albanian bulgarian ukrainian belarusian bosnian macedonian ' +
    'mongolia india china japan canada mexico brazil chile peru cuba egypt libya syria iran iraq israel ' +
    'jordan oman yemen kenya ghana mali chad niger nigeria sudan korea nepal laos burma kazakhstan ' +
    'avaria sardinia sicily corsica crete istria varna tarnow tarnobrzeg'
  ).split(' '),
);

/** A few famous real cities the syllable sets could produce. */
const REAL_CITIES = new Set(
  (
    'riga oslo minsk brest pinsk tartu posen lund bergen malmo borg varna bitola stara kaunas vilnius ' +
    'sofia ruse rovno dubno kolin brno pilsen zagreb split osijek pecs gyor szeged eger tata zalaegerszeg ' +
    'arad deva sibiu turda bistrita galati braila tulcea buzau focsani hamburg bremen hannover linz graz ' +
    'salzburg innsbruck dornbirn vitebsk gomel grodno lublin lodz torun plock kalisz radom kielce sopot ' +
    'narva parnu liepaja jelgava ventspils daugavpils siauliai klaipeda panevezys alytus marijampole ' +
    'tallinn helsinki turku tampere stockholm uppsala gavle vasteras orebro linkoping umea kiruna ' +
    'trondheim stavanger tromso bodo aalborg aarhus odense esbjerg randers kolding vejle horsens ' +
    'freiburg rosenheim steinfurt pescara florina siauliai'
  ).split(' '),
);

const VOWELS = 'aeiouy';
const isVowel = (c: string): boolean => VOWELS.includes(c);

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Joins root + suffix the way a place name would be spoken: no tripled letters or consonant pile-ups. */
export function join(root: string, suffix: string, link: string, maxCluster: number): string {
  const r = root.toLowerCase();
  let s = suffix.toLowerCase();
  if (r.endsWith(s.charAt(0)) && !isVowel(s.charAt(0))) s = s.slice(1);
  let head = r;
  if (isVowel(r.charAt(r.length - 1)) && isVowel(s.charAt(0))) head = r.slice(0, -1);
  let tail = 0;
  while (tail < head.length && !isVowel(head.charAt(head.length - 1 - tail))) tail++;
  let lead = 0;
  while (lead < s.length && !isVowel(s.charAt(lead))) lead++;
  const glue = tail + lead > maxCluster ? link : '';
  return capitalise(head + glue + s);
}

function isReal(word: string): boolean {
  const w = word.toLowerCase();
  return REAL_WORDS.has(w) || REAL_CITIES.has(w);
}

/** Generates unique names (across cities and regions of one map) in any style. */
export class NameBook {
  private readonly rng: Rng;
  private readonly used = new Set<string>();

  constructor(rng: Rng) {
    this.rng = rng;
  }

  /** Reserve a name produced elsewhere (e.g. faction land names) so places don't repeat it. */
  reserve(name: string): void {
    this.used.add(name.toLowerCase());
  }

  city(style: NameStyle, where: { coastal: boolean; river: boolean }): string {
    const rng = this.rng;
    return this.unique(() => {
      let pool = style.suffixes;
      if (where.coastal && rng.chance(0.45)) pool = style.coastal;
      else if (where.river && style.river.length > 0 && rng.chance(0.35)) pool = style.river;
      let base = join(rng.pick(style.roots), rng.pick(pool), style.link, style.maxCluster);
      if (style.fused.length > 0 && rng.chance(0.08)) base = rng.pick(style.fused) + base.toLowerCase();
      if (base.length > 13 || isReal(base)) return null;
      const qualified = style.qualifiers.length > 0 && rng.chance(0.07);
      return { base, name: qualified ? `${rng.pick(style.qualifiers)} ${base}` : base };
    });
  }

  /** Province name; `epithet` (e.g. "Heights") is appended sometimes to reflect dominant terrain. */
  region(style: NameStyle, epithet: string | null): string {
    const rng = this.rng;
    return this.unique(() => {
      const base = join(rng.pick(style.roots), rng.pick(style.regionSuffixes), style.link, style.maxCluster);
      if (base.length > 12 || isReal(base)) return null;
      if (epithet && rng.chance(0.5)) return { base, name: `${base} ${epithet}` };
      if (rng.chance(0.12)) return { base, name: `${rng.chance(0.5) ? 'Upper' : 'Lower'} ${base}` };
      return { base, name: base };
    });
  }

  /**
   * Draws candidates until one is new. Both the full name and its bare base are reserved, so
   * "Zlatica" and "Stara Zlatica" (or "Dornmark" and "Dornmark Heights") never coexist.
   */
  private unique(make: () => { base: string; name: string } | null): string {
    for (let attempt = 0; attempt < 200; attempt++) {
      const c = make();
      if (!c || this.used.has(c.base.toLowerCase()) || this.used.has(c.name.toLowerCase())) continue;
      this.used.add(c.base.toLowerCase());
      this.used.add(c.name.toLowerCase());
      return c.name;
    }
    // Practically unreachable (thousands of combinations per style); stay unique anyway.
    for (let i = 2; ; i++) {
      const name = `${make()?.name ?? 'Novo'} ${i}`;
      if (!this.used.has(name.toLowerCase())) {
        this.used.add(name.toLowerCase());
        return name;
      }
    }
  }
}

// ---- factions -------------------------------------------------------------------------------

export interface FactionPlan extends FactionIdentity {
  style: NameStyle;
  /** Bare country name ("Varnia"), used for theatre and region naming. */
  land: string;
}

/** Government forms; {L} = land name, {A} = adjective, {N} = land if attributive, else adjective. */
const FORMS: readonly { pattern: string; styles?: readonly NameStyle['id'][] }[] = [
  { pattern: 'Kingdom of {L}' },
  { pattern: 'Republic of {L}' },
  { pattern: '{N} Republic' },
  { pattern: 'Grand Duchy of {L}' },
  { pattern: 'Principality of {L}' },
  { pattern: 'Free State of {L}' },
  { pattern: 'United Provinces of {L}' },
  { pattern: '{A} Union' },
  { pattern: '{A} Commonwealth' },
  { pattern: '{A} Federation' },
  { pattern: '{A} Confederation' },
  { pattern: 'Duchy of {L}' },
  { pattern: 'Tsardom of {L}', styles: ['slavic'] },
  { pattern: 'Regency of {L}' },
];

/** Land names that work attributively as they are ("Ostmark Republic", not "Ostmarker Republic"). */
const GERMANIC_LAND = /(mark|land|heim|au)$/;

/** "Varnia" → "Varnian", "Ostmark" → "Ostmarker", "Kaldor" → "Kaldoran", "Szolany" → "Szolanyi". */
export function adjectiveOf(land: string, style: NameStyle): string {
  if (style.id === 'danubian' && !land.endsWith('ia')) return `${land}i`;
  if (land.endsWith('a')) return `${land}n`;
  if (GERMANIC_LAND.test(land)) return `${land}er`;
  if (land.endsWith('e')) return `${land.slice(0, -1)}ian`;
  return `${land}an`;
}

/**
 * Deterministic faction plans for a seed: one distinct style per faction, distinct initials where
 * possible, distinct government forms. Map generation and the session both derive from this.
 */
export function planFactions(seed: number, count: number): FactionPlan[] {
  const rng = stageRng(seed, 'factions');
  const styles = rng.shuffle(STYLES.slice());
  const forms = rng.shuffle(FORMS.slice());
  const initials = new Set<string>();
  const plans: FactionPlan[] = [];
  for (let i = 0; i < count; i++) {
    const style = styles[i % styles.length];
    let land = '';
    let root = '';
    for (let attempt = 0; attempt < 80; attempt++) {
      const r = rng.pick(style.roots);
      const candidate = join(r, rng.pick(style.landSuffixes), style.link, style.maxCluster);
      if (candidate.length > 10 || isReal(candidate) || isReal(adjectiveOf(candidate, style))) continue;
      if (plans.some((p) => p.land === candidate)) continue;
      // Keep the first acceptable candidate; prefer one whose initial is still free.
      if (!land || !initials.has(candidate.charAt(0))) {
        land = candidate;
        root = r;
      }
      if (!initials.has(land.charAt(0))) break;
    }
    initials.add(land.charAt(0));
    const formIdx = forms.findIndex((f) => !f.styles || f.styles.includes(style.id));
    const form = forms.splice(formIdx, 1)[0];
    let adjective = adjectiveOf(land, style);
    // Germanic/Nordic unions read best with the "-ish" adjective of the bare root ("Brennish Union");
    // only for roots that carry it (≥ 4 letters, consonant ending), so no "Treish" or "Ekish".
    const ishRoot = root.length >= 4 && !isVowel(root.charAt(root.length - 1).toLowerCase());
    if (form.pattern.startsWith('{A}') && (style.id === 'germanic' || style.id === 'nordic') && ishRoot) {
      if (rng.chance(0.6)) adjective = `${root}ish`;
    }
    const name = form.pattern
      .replace('{L}', land)
      .replace('{A}', adjective)
      .replace('{N}', GERMANIC_LAND.test(land) ? land : adjective);
    plans.push({ name, adjective, style, land });
  }
  return plans;
}

/** Theatre title, e.g. "The Varnian Marches" or "The Kessling Gap". */
export function theatreName(
  rng: Rng,
  plans: readonly FactionPlan[],
  regionNames: readonly string[],
  highland: boolean,
): string {
  const a = rng.pick(plans).adjective;
  const others = plans.filter((p) => p.adjective !== a);
  const b = others.length > 0 ? rng.pick(others).adjective : a;
  // Bare province name: "Upper Dornmark Heights" → "Dornmark".
  const region =
    regionNames.length > 0 ? rng.pick(regionNames).replace(/^(Upper|Lower) | (Heights|Woods|Fens)$/g, '') : a;
  const forms = [
    `The ${a} Marches`,
    `The ${a} Frontier`,
    `The ${a}-${b} Borderlands`,
    `The ${region} Gap`,
    `The Plains of ${region}`,
    `The ${region} Salient`,
    `The ${a} ${highland ? 'Highlands' : 'Lowlands'}`,
    `The ${region} Corridor`,
  ];
  return rng.pick(forms);
}
